import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type Category = {
  id: string;
  slug: string;
  name: string;
  type: "product" | "favorite" | "affiliate" | "both";
  icon: string | null;
  color: string | null;
  sortOrder: number;
};

type CategorySortKey = "name" | "slug" | "type" | "sort";

type FormState = {
  name: string;
  slug: string;
  type: "product" | "favorite" | "affiliate" | "both";
  icon: string;
  color: string;
  sortOrder: string;
};

const empty: FormState = {
  name: "",
  slug: "",
  type: "both",
  icon: "",
  color: "",
  sortOrder: "0",
};

function toForm(c: Category): FormState {
  return {
    name: c.name,
    slug: c.slug,
    type: c.type,
    icon: c.icon ?? "",
    color: c.color ?? "",
    sortOrder: c.sortOrder.toString(),
  };
}

function categorySearchText(category: Category) {
  return [category.name, category.slug, category.type, category.icon, category.color, formatInteger(category.sortOrder)]
    .filter(Boolean)
    .join(" ");
}

const categorySorters: Record<CategorySortKey, (row: Category) => string | number> = {
  name: (row) => row.name,
  slug: (row) => row.slug,
  type: (row) => row.type,
  sort: (row) => row.sortOrder,
};

export default function AdminCategories() {
  const [list, setList] = useState<Category[]>([]);
  const [editing, setEditing] = useState<Category | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ categories: Category[] }>("/api/admin/categories");
    setList(r.categories);
  }

  useEffect(() => {
    refresh().finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm({ ...empty });
    setEditing("new");
    setErr(null);
  }

  function startEdit(cat: Category) {
    setForm(toForm(cat));
    setEditing(cat);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name: form.name,
      slug: form.slug || undefined,
      type: form.type,
      icon: form.icon || null,
      color: form.color || null,
      sortOrder: parseInt(form.sortOrder || "0", 10),
    };
    try {
      if (editing === "new") await api.post("/api/admin/categories", payload);
      else if (editing) await api.put(`/api/admin/categories/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(cat: Category) {
    if (!confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/categories/${cat.id}`);
    await refresh();
  }

  const filters = useMemo(
    () => [(row: Category) => typeFilter === "all" || row.type === typeFilter],
    [typeFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Category, CategorySortKey>({
    rows: list,
    search,
    initialSort: { key: "sort", direction: "asc" },
    sorters: categorySorters,
    searchText: categorySearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Categories</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New category
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New category" : editing.name}
            eyebrow={editing === "new" ? "Create category" : "Edit category"}
            backLabel="Back to categories"
            onBack={() => setEditing(null)}
          />
          <div className="grid2">
            <Field
              label="Name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <Field
              label="Slug (optional)"
              value={form.slug}
              onChange={(v) => setForm({ ...form, slug: v })}
            />
          </div>
          <div className="grid2">
            <label className="fld">
              <span>Type</span>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    type: e.target.value as FormState["type"],
                  })
                }
              >
                <option value="both">Both</option>
                <option value="product">Shop</option>
                <option value="favorite">Favorites</option>
                <option value="affiliate">Affiliate</option>
              </select>
            </label>
            <Field
              label="Sort order"
              value={form.sortOrder}
              onChange={(v) => setForm({ ...form, sortOrder: v })}
            />
          </div>
          <div className="grid2">
            <Field
              label="Icon (optional)"
              value={form.icon}
              onChange={(v) => setForm({ ...form, icon: v })}
            />
            <Field
              label="Color (optional)"
              value={form.color}
              onChange={(v) => setForm({ ...form, color: v })}
            />
          </div>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search categories..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: "all", label: "All types" },
                { value: "both", label: "Both" },
                { value: "product", label: "Shop" },
                { value: "favorite", label: "Favorites" },
                { value: "affiliate", label: "Affiliate" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No categories yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No categories match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={requestSort} />
                  <SortableTh label="Slug" sortKey="slug" sort={sort} onSort={requestSort} />
                  <SortableTh label="Type" sortKey="type" sort={sort} onSort={requestSort} />
                  <SortableTh label="Sort" sortKey="sort" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((cat) => (
                  <tr key={cat.id}>
                    <td>
                      <strong>{cat.name}</strong>
                    </td>
                    <td>
                      <span className="slug">{cat.slug}</span>
                    </td>
                    <td>{cat.type}</td>
                    <td>{formatInteger(cat.sortOrder)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className="btn-ghost sm"
                        onClick={() => startEdit(cat)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-danger sm"
                        onClick={() => remove(cat)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
`;
